import React, { useState } from 'react';
import { Plus, Trash2, Eye, EyeOff } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

export default function RatesTab({ metalRates, fetchData, refreshRates, metalCaratOptions }) {
  const [rateForm, setRateForm] = useState({
    metal_type: 'Gold',
    purity: '22k',
    rate_per_gram: '',
    gst_percent: '3',
    show_on_homepage: false,
    display_label: ''
  });

  // Get purity options based on metal type
  const getPurityOptions = (metalType) => {
    if (metalType === 'Gold') {
      return metalCaratOptions || ['24k', '22k', '18k', '14k', '9k'];
    } else if (metalType === 'Silver') {
      return ['999', '925', '900', '800'];
    } else if (metalType === 'Platinum') {
      return ['950', '900', '850'];
    } else if (metalType === 'Diamond') {
      return ['Natural', 'Lab-grown'];
    }
    return ['Standard'];
  };

  const handleRateSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        metal_type: rateForm.metal_type,
        purity: rateForm.purity,
        rate_per_gram: parseFloat(rateForm.rate_per_gram),
        gst_percent: parseFloat(rateForm.gst_percent),
        show_on_homepage: rateForm.show_on_homepage,
        display_label: rateForm.display_label || `${rateForm.purity} ${rateForm.metal_type}`
      };
      
      await api.post('/metal-rates', data);
      toast.success('Metal rate saved — auto-sync has been turned off');
      setRateForm({
        metal_type: 'Gold',
        purity: '22k',
        rate_per_gram: '',
        gst_percent: '3',
        show_on_homepage: false,
        display_label: ''
      });
      fetchData();
      if (refreshRates) refreshRates();
    } catch (error) {
      toast.error('Failed to save rate');
    }
  };

  const toggleHomepageDisplay = async (rate) => {
    try {
      await api.post('/metal-rates', {
        ...rate,
        show_on_homepage: !rate.show_on_homepage
      });
      toast.success(rate.show_on_homepage ? 'Removed from homepage' : 'Added to homepage widget');
      fetchData();
      if (refreshRates) refreshRates();
    } catch (error) {
      toast.error('Failed to update rate');
    }
  };

  const deleteRate = async (metalType, purity) => {
    if (!window.confirm(`Delete ${purity} ${metalType} rate?`)) return;
    try {
      await api.delete(`/metal-rates/${metalType}/${purity}`);
      toast.success('Rate deleted');
      fetchData();
      if (refreshRates) refreshRates();
    } catch (error) {
      toast.error('Failed to delete rate');
    }
  };

  // Group rates by metal type
  const groupedRates = metalRates.reduce((acc, rate) => {
    const type = rate.metal_type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(rate);
    return acc;
  }, {});

  // Get rates shown on homepage
  const homepageRates = metalRates.filter(r => r.show_on_homepage);

  return (
    <div data-testid="rates-tab">
      <h2 className="font-serif text-2xl font-semibold text-[#1a3a5c] mb-6">Metal Rates Management</h2>
      
      {/* Homepage Widget Preview */}
      {homepageRates.length > 0 && (
        <div className="bg-gradient-to-r from-[#C9A967]/10 to-[#1a3a5c]/10 p-4 rounded-lg mb-6 border border-[#C9A967]/30">
          <h3 className="font-medium text-[#1a3a5c] mb-3 flex items-center">
            <Eye className="h-4 w-4 mr-2" />
            Homepage Widget Preview ({homepageRates.length} rates will rotate)
          </h3>
          <div className="flex flex-wrap gap-2">
            {homepageRates.map((rate) => (
              <span key={`${rate.metal_type}-${rate.purity}`} className="px-3 py-1 bg-white rounded-full text-sm border border-gray-200">
                {rate.display_label || `${rate.purity} ${rate.metal_type}`}: ₹{rate.rate_per_gram?.toLocaleString('en-IN')}/g
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Add/Update Rate Form */}
      <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6">
        <h3 className="font-serif text-xl font-semibold text-[#1a3a5c] mb-4">Add / Update Rate</h3>
        <form onSubmit={handleRateSubmit} className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Metal Type</label>
            <select
              value={rateForm.metal_type}
              onChange={(e) => {
                const newType = e.target.value;
                const purities = getPurityOptions(newType);
                setRateForm({ 
                  ...rateForm, 
                  metal_type: newType,
                  purity: purities[0]
                });
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              data-testid="rate-metal-type-select"
            >
              <option value="Gold">Gold</option>
              <option value="Silver">Silver</option>
              <option value="Diamond">Diamond</option>
              <option value="Platinum">Platinum</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Purity</label>
            <select
              value={rateForm.purity}
              onChange={(e) => setRateForm({ ...rateForm, purity: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              data-testid="rate-purity-select"
            >
              {getPurityOptions(rateForm.metal_type).map(purity => (
                <option key={purity} value={purity}>{purity}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Rate/gram (₹)</label>
            <input
              type="number"
              step="0.01"
              value={rateForm.rate_per_gram}
              onChange={(e) => setRateForm({ ...rateForm, rate_per_gram: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              required
              placeholder="e.g., 6500"
              data-testid="rate-per-gram-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">GST (%)</label>
            <input
              type="number"
              step="0.01"
              value={rateForm.gst_percent}
              onChange={(e) => setRateForm({ ...rateForm, gst_percent: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              required
              data-testid="gst-percent-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Display Label</label>
            <input
              type="text"
              value={rateForm.display_label}
              onChange={(e) => setRateForm({ ...rateForm, display_label: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder={`${rateForm.purity} ${rateForm.metal_type}`}
              data-testid="display-label-input"
            />
          </div>

          <div className="flex flex-col justify-end">
            <label className="flex items-center space-x-2 mb-2">
              <input
                type="checkbox"
                checked={rateForm.show_on_homepage}
                onChange={(e) => setRateForm({ ...rateForm, show_on_homepage: e.target.checked })}
                className="rounded border-gray-300 text-[#1a3a5c] focus:ring-[#1a3a5c]"
                data-testid="show-homepage-checkbox"
              />
              <span className="text-sm text-gray-700">Show on Homepage</span>
            </label>
            <button
              type="submit"
              className="bg-[#1a3a5c] text-white px-4 py-2 rounded-sm hover:bg-[#1f4268] transition-colors flex items-center justify-center space-x-2"
              data-testid="save-rate-button"
            >
              <Plus className="h-4 w-4" />
              <span>Save Rate</span>
            </button>
          </div>
        </form>
      </div>

      {/* Rates List by Metal Type */}
      {Object.entries(groupedRates).map(([metalType, rates]) => (
        <div key={metalType} className="mb-6">
          <h3 className="font-serif text-lg font-semibold text-[#1a3a5c] mb-3">{metalType} Rates</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {rates.map((rate) => (
              <div 
                key={`${rate.metal_type}-${rate.purity}`} 
                className={`bg-white p-4 rounded-lg border ${rate.show_on_homepage ? 'border-[#C9A967] ring-1 ring-[#C9A967]/30' : 'border-gray-200'}`}
                data-testid={`rate-${rate.metal_type}-${rate.purity}`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-semibold text-[#1a3a5c]">
                      {rate.display_label || `${rate.purity} ${rate.metal_type}`}
                    </h4>
                    <p className="text-xs text-gray-500">{rate.purity} purity</p>
                  </div>
                  {rate.show_on_homepage && (
                    <span className="px-2 py-0.5 bg-[#C9A967]/20 text-[#1a3a5c] text-xs rounded-full">
                      On Homepage
                    </span>
                  )}
                </div>
                
                <p className="text-2xl font-bold text-gray-900 mb-1">
                  ₹{rate.rate_per_gram?.toLocaleString('en-IN')}
                </p>
                <p className="text-sm text-gray-600">per gram • GST: {rate.gst_percent}%</p>
                <p className="text-xs text-gray-400 mt-2">
                  Updated: {rate.updated_at ? new Date(rate.updated_at).toLocaleDateString() : 'N/A'}
                </p>

                <div className="flex space-x-2 mt-3">
                  <button
                    onClick={() => toggleHomepageDisplay(rate)}
                    className={`flex-1 px-3 py-1.5 rounded-sm text-sm transition-colors flex items-center justify-center space-x-1 ${
                      rate.show_on_homepage 
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                        : 'bg-[#C9A967]/20 text-[#1a3a5c] hover:bg-[#C9A967]/30'
                    }`}
                    data-testid={`toggle-homepage-${rate.metal_type}-${rate.purity}`}
                  >
                    {rate.show_on_homepage ? (
                      <>
                        <EyeOff className="h-4 w-4" />
                        <span>Hide</span>
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4" />
                        <span>Show</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => deleteRate(rate.metal_type, rate.purity)}
                    className="px-3 py-1.5 bg-red-100 text-red-700 rounded-sm text-sm hover:bg-red-200 transition-colors"
                    data-testid={`delete-rate-${rate.metal_type}-${rate.purity}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {metalRates.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-600">No metal rates set. Add your first rate above.</p>
        </div>
      )}
    </div>
  );
}
